import { useEffect, useRef, useState } from "react";
import { Save, Check, Loader2, Eye, EyeOff, RotateCw } from "lucide-react";
import { Toaster, toast } from "sonner";
import { useSiteContent } from "@/admin/SiteContent";

/** Generic shell: title + save button. Pass children = form fields. */
export const EditorShell = ({
  title,
  description,
  section,
  value,
  setValue,
  validate,
  children,
}) => {
  const { saveSection } = useSiteContent();
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const iframeRef = useRef(null);

  const onSave = async () => {
    if (validate) {
      const err = validate(value);
      if (err) {
        toast.error(err);
        return;
      }
    }
    setSaving(true);
    try {
      await saveSection(section, value);
      setSavedAt(Date.now());
      toast.success("Salvato. Modifiche live sul sito.", {
        id: "editor-save-success",
        className: "editor-toast-success",
      });
      // Auto-reload preview iframe to show changes
      setReloadKey((k) => k + 1);
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || e.message || "Errore durante il salvataggio",
      );
    } finally {
      setSaving(false);
    }
  };

  /* Cmd/Ctrl + S to save */
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "s") {
        e.preventDefault();
        if (!saving) onSave();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [value, saving]);

  return (
    <div data-testid={`editor-${section}`}>
      <Toaster
        position="bottom-right"
        toastOptions={{
          style: {
            background: "rgba(16,16,22,0.95)",
            border: "1px solid rgba(212,175,55,0.4)",
            color: "#F5F5F5",
          },
        }}
      />
      <div className="flex flex-wrap items-end justify-between gap-4 mb-9">
        <div>
          <p className="font-mono-pl text-[10px] uppercase tracking-[0.32em] text-[#D4AF37]">
            Sezione: {section}
          </p>
          <h1 className="font-display text-3xl sm:text-4xl uppercase tracking-wide mt-2 text-[#F5F5F5]">
            {title}
          </h1>
          {description && (
            <p className="mt-2 text-sm text-[#B6B6BE] max-w-2xl">{description}</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowPreview((v) => !v)}
            data-testid={`editor-${section}-preview-toggle`}
            className="inline-flex items-center gap-2 rounded-full px-4 py-3 text-xs uppercase tracking-[0.2em] border border-[#00D4FF]/40 text-[#00D4FF] hover:bg-[#00D4FF]/10 transition-colors"
          >
            {showPreview ? <EyeOff size={13} /> : <Eye size={13} />}
            {showPreview ? "Nascondi preview" : "Anteprima live"}
          </button>
          <button
            onClick={onSave}
            disabled={saving}
            data-testid={`editor-${section}-save`}
            className="btn-gold inline-flex items-center gap-2 rounded-full px-6 py-3 text-xs font-bold uppercase tracking-wider disabled:opacity-60"
          >
            {saving ? (
              <Loader2 size={14} className="animate-spin" />
            ) : savedAt ? (
              <Check size={14} strokeWidth={2.6} />
            ) : (
              <Save size={14} strokeWidth={2.4} />
            )}
            {saving ? "Salvataggio…" : "Salva (⌘S)"}
          </button>
        </div>
      </div>

      <div className={`grid gap-6 ${showPreview ? "lg:grid-cols-2" : "grid-cols-1"}`}>
        <div
          className="rounded-3xl p-7 sm:p-10 min-w-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(22,22,29,0.6), rgba(11,11,15,0.85))",
            border: "1px solid rgba(255,255,255,0.06)",
          }}
        >
          {children}
        </div>

        {showPreview && (
          <div
            className="rounded-3xl overflow-hidden relative min-w-0"
            style={{
              border: "1px solid rgba(0,212,255,0.3)",
              boxShadow: "0 0 30px -10px rgba(0,212,255,0.45)",
              minHeight: 720,
            }}
          >
            <div
              className="absolute top-0 inset-x-0 z-10 flex items-center justify-between px-4 py-2.5"
              style={{
                background: "rgba(8,8,12,0.92)",
                borderBottom: "1px solid rgba(0,212,255,0.2)",
                backdropFilter: "blur(8px)",
              }}
            >
              <p className="font-mono-pl text-[10px] uppercase tracking-[0.28em] text-[#00D4FF]">
                Anteprima live
              </p>
              <button
                onClick={() => setReloadKey((k) => k + 1)}
                className="text-[#B6B6BE] hover:text-[#F5F5F5] transition-colors"
                aria-label="Ricarica"
              >
                <RotateCw size={13} />
              </button>
            </div>
            <iframe
              key={reloadKey}
              ref={iframeRef}
              src={`/?cms=1#${section === "hero" ? "top" : section}`}
              title="Anteprima"
              className="w-full h-full absolute inset-0 pt-10"
              style={{ minHeight: 720 }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

/** Common form components */
export const Field = ({ label, hint, children, testid }) => (
  <label className="block mb-5" data-testid={testid}>
    <span className="block text-[10px] uppercase tracking-[0.26em] text-[#D4AF37] mb-2">
      {label}
    </span>
    {children}
    {hint && <span className="block mt-1.5 text-xs text-[#B6B6BE]/70">{hint}</span>}
  </label>
);

export const TextInput = ({ value, onChange, placeholder, type = "text", testid }) => (
  <input
    type={type}
    value={value || ""}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    data-testid={testid}
    className="search-input w-full px-4 py-3 rounded-xl bg-[#0B0B0F]/90 border border-white/10 text-sm text-[#F5F5F5] placeholder:text-[#B6B6BE]/55 focus:outline-none focus:border-[#D4AF37]/55"
  />
);

export const TextArea = ({ value, onChange, placeholder, rows = 4, testid }) => (
  <textarea
    value={value || ""}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    rows={rows}
    data-testid={testid}
    className="search-input w-full px-4 py-3 rounded-xl bg-[#0B0B0F]/90 border border-white/10 text-sm text-[#F5F5F5] placeholder:text-[#B6B6BE]/55 focus:outline-none focus:border-[#D4AF37]/55 resize-y leading-relaxed"
  />
);

/** Hook to get a section's value with live editing state */
export const useSectionState = (section) => {
  const { content } = useSiteContent();
  const [value, setValue] = useState(null);

  useEffect(() => {
    if (content && content[section] !== undefined) {
      setValue(JSON.parse(JSON.stringify(content[section])));
    }
  }, [content, section]);

  return [value, setValue];
};
